/**
 * Word-level diff for before/after resume text.
 *
 * The comparison surfaces currently print "Before: … / After: …" as two blocks
 * of prose. When an AI pass rewrites a bullet, the two lines are ~90% identical
 * and the reader has to spot the difference by eye — which is the one job the
 * comparison exists to do for them.
 *
 * This produces the token stream a GitHub-style diff renders from: runs of
 * unchanged, removed, and added words, in reading order.
 *
 * Word-level rather than character-level on purpose. Character diffs of prose
 * shred words into fragments ("manag|ed" -> "manag|ing") which is noisier than
 * showing the whole word replaced. Whitespace rides along with its word so the
 * rebuilt text keeps its spacing.
 */

export type DiffOp = 'equal' | 'added' | 'removed';

export interface DiffToken {
  op: DiffOp;
  /** The text to render. For `equal` runs this is the AFTER spelling. */
  value: string;
  /**
   * The BEFORE-side text of an `equal` run, when it differed.
   *
   * Equal runs match loosely (case, trailing punctuation, spacing), so the two
   * sides can hold different characters while still being "the same word". The
   * panel shows the after spelling, but the before text has to survive
   * somewhere or a before/after reconstruction silently reports the wrong
   * original — including its whitespace, which decides where line breaks fall.
   */
  beforeValue?: string;
}

/**
 * Split into words, keeping trailing whitespace attached so tokens can be
 * concatenated back into the original string without guessing at spacing.
 */
export function tokenizeWords(text: string): string[] {
  if (!text) return [];
  return text.match(/\S+\s*/g) ?? [];
}

/** Compare ignoring case, surrounding space, and trailing punctuation. */
const comparableToken = (token: string): string =>
  token.trim().toLowerCase().replace(/[.,;:!?]+$/, '');

/**
 * Longest common subsequence over the token lists.
 *
 * Bounded deliberately: this is O(n*m) in time and memory, and resume fields are
 * short. A pathological input (someone pasting an entire CV into one field)
 * would otherwise allocate a matrix big enough to matter on the client, so past
 * the cap we fall back to "everything removed, everything added" — still
 * correct, just not minimal.
 */
const MAX_TOKENS = 600;

function lcsTable(a: string[], b: string[]): Uint32Array {
  const width = b.length + 1;
  const table = new Uint32Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i * width + j] =
        comparableToken(a[i]) === comparableToken(b[j])
          ? table[(i + 1) * width + (j + 1)] + 1
          : Math.max(table[(i + 1) * width + j], table[i * width + (j + 1)]);
    }
  }
  return table;
}

/**
 * Diff two strings into ordered runs of unchanged / removed / added text.
 *
 * Adjacent tokens with the same op are merged, so a rewritten phrase renders as
 * one red span and one green span rather than a stutter of per-word marks.
 */
export function diffWords(before: string, after: string): DiffToken[] {
  const a = tokenizeWords(before ?? '');
  const b = tokenizeWords(after ?? '');

  if (a.length === 0 && b.length === 0) return [];
  if (a.length === 0) return mergeTokens(b.map((value) => ({ op: 'added' as const, value })));
  if (b.length === 0) return mergeTokens(a.map((value) => ({ op: 'removed' as const, value })));

  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    return mergeTokens([
      ...a.map((value) => ({ op: 'removed' as const, value })),
      ...b.map((value) => ({ op: 'added' as const, value })),
    ]);
  }

  const width = b.length + 1;
  const table = lcsTable(a, b);
  const out: DiffToken[] = [];

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (comparableToken(a[i]) === comparableToken(b[j])) {
      // Equal by the loose comparison, so show the AFTER spelling — otherwise a
      // pure capitalisation or punctuation fix would silently keep the old text.
      out.push({ op: 'equal', value: b[j], beforeValue: a[i] });
      i += 1;
      j += 1;
    } else if (table[(i + 1) * width + j] >= table[i * width + (j + 1)]) {
      out.push({ op: 'removed', value: a[i] });
      i += 1;
    } else {
      out.push({ op: 'added', value: b[j] });
      j += 1;
    }
  }
  while (i < a.length) {
    out.push({ op: 'removed', value: a[i] });
    i += 1;
  }
  while (j < b.length) {
    out.push({ op: 'added', value: b[j] });
    j += 1;
  }

  return mergeTokens(out);
}

/** Collapse consecutive same-op tokens into single runs. */
function mergeTokens(tokens: DiffToken[]): DiffToken[] {
  const merged: DiffToken[] = [];
  tokens.forEach((token) => {
    const last = merged[merged.length - 1];
    if (last && last.op === token.op) {
      last.value += token.value;
      // Both sides accumulate together, so a merged run still rebuilds exactly.
      if (last.op === 'equal') {
        last.beforeValue = (last.beforeValue ?? '') + (token.beforeValue ?? token.value);
      }
      return;
    }
    merged.push({ ...token });
  });
  return merged;
}

/** Rebuild one side of the comparison from a token stream. */
export function rebuildSide(tokens: DiffToken[], side: 'before' | 'after'): string {
  return tokens
    .filter((token) => token.op === 'equal' || token.op === (side === 'before' ? 'removed' : 'added'))
    .map((token) =>
      side === 'before' && token.op === 'equal' ? token.beforeValue ?? token.value : token.value
    )
    .join('');
}

export interface DiffSummary {
  addedWords: number;
  removedWords: number;
  /** True when the two texts differ only by case, punctuation or spacing. */
  cosmeticOnly: boolean;
  unchanged: boolean;
}

/**
 * Headline counts for a change row.
 *
 * `cosmeticOnly` earns its place: an AI pass that only re-cased a heading
 * should not be presented with the same weight as one that rewrote a bullet,
 * and the raw token counts cannot tell those apart.
 */
export function summarizeDiff(tokens: DiffToken[]): DiffSummary {
  let addedWords = 0;
  let removedWords = 0;
  tokens.forEach((token) => {
    if (token.op === 'added') addedWords += tokenizeWords(token.value).length;
    if (token.op === 'removed') removedWords += tokenizeWords(token.value).length;
  });

  const changed = addedWords > 0 || removedWords > 0;
  return {
    addedWords,
    removedWords,
    unchanged: !changed,
    cosmeticOnly: false,
  };
}

/**
 * Whole-value comparison for the cosmetic check — done on the strings rather
 * than the token stream, because the token stream has already normalised away
 * exactly the differences we are trying to detect.
 */
export function isCosmeticChange(before: string, after: string): boolean {
  const normalize = (value: string) =>
    (value ?? '').toLowerCase().replace(/[.,;:!?]+/g, '').replace(/\s+/g, ' ').trim();
  return (before ?? '') !== (after ?? '') && normalize(before) === normalize(after);
}
